import { saveAndShare } from '@/features/sharing/ShareSheet'
import { toast } from '@/components/ui'
import { ErrorNotice } from '@/components/ui/Feedback'
import type { ReactNode } from 'react'
import {
  Check,
  CircleCheck,
  Clock,
  Square,
  Volume2,
  X,
} from 'lucide-react'

import type { LibraryStatus, SearchResult } from '@/api/types'
import { usePlayer } from '@/audio/store'
import { songActions } from '@/components/songActions'
import { TrackRow } from '@/components/TrackRow'
import { ProviderBadge } from '@/components/ProviderIcon'
import { IconButton, Spinner } from '@/components/ui'
import { searchArtistHref } from '@/features/artists/artistHref'

import { SelectCheckbox } from './SelectCheckbox'

export type ResultAction = 'play' | 'next' | 'queue' | 'download'

export interface RowError {
  message: string
  retry: () => void
}

export interface SearchResultRowProps {
  wrapRow?: (row: ReactNode) => ReactNode
  result: SearchResult
  /** Derived from the library state plus any live download job. */
  status?: LibraryStatus
  /** 0-100, only while a job is running. */
  progress?: number
  /** True while this row's own request is in flight. */
  busy?: boolean
  error?: RowError
  /** Show the source on desktop and mobile, including filtered searches. */
  showProvider?: boolean
  providerName: string
  /** Selection mode is on for the whole list, so every row shows a tick box. */
  selectable?: boolean
  selected?: boolean
  /** `shiftKey` is true when the user extended a range from the last row touched. */
  onSelectChange?: (shiftKey: boolean) => void
  /** Turns selection mode on from this row. Omitted once it is already on. */
  onStartSelecting?: () => void
  onAction: (action: ResultAction) => void
  onAddToPlaylist: () => void
  isFavorite?: boolean
  onToggleFavorite?: () => void
  onDismissSuggestion?: () => void
  onDismissError: () => void
}

export function SearchResultRow({
  wrapRow,
  result,
  status,
  progress,
  busy = false,
  error,
  showProvider = true,
  providerName,
  selectable = false,
  selected = false,
  onSelectChange,
  onStartSelecting,
  onAction,
  onAddToPlaylist,
  onDismissError,
  onDismissSuggestion,
  isFavorite,
  onToggleFavorite,
}: SearchResultRowProps) {

  const previewKey = `${result.provider}:${result.external_id}`
  const startPreview = usePlayer((state) => state.startPreview)
  const stopPreview = usePlayer((state) => state.stopPreview)
  const previewing = usePlayer((state) => state.preview?.key === previewKey)
  const isActive = usePlayer((state) => {
    const current = state.current()
    return !!current && ((result.track_id != null && current.trackId === result.track_id) || (current.provider === result.provider && current.externalId === result.external_id))
  })
  const isPlaying = usePlayer((state) => state.isPlaying)

  const trailing = (
    <span className="flex items-center">
      {/* The point of the whole feature: hear it before the server fetches
          anything. Absent for providers that offer no clip — YouTube — where
          the only way to hear it really is to download it. */}
      {result.preview_url ? (
        <IconButton
          size="sm"
          aria-label={
            previewing
              ? `Stop previewing ${result.title}`
              : `Preview ${result.title} by ${result.artist_name}`
          }
          className={`hidden sm:inline-flex ${previewing ? 'text-accent' : ''}`}
          onClick={() =>
            previewing
              ? stopPreview()
              : startPreview({
                  key: previewKey,
                  title: result.title,
                  artistName: result.artist_name,
                  url: result.preview_url as string,
                })
          }
        >
          {previewing ? (
            <Square className="h-4 w-4 fill-current" aria-hidden="true" />
          ) : (
            <Volume2 className="h-4 w-4" aria-hidden="true" />
          )}
        </IconButton>
      ) : null}

      {busy ? (
        <Spinner size="sm" className="mx-1.5 text-text-subtle" label={`Adding ${result.title}`} />
      ) : result.in_library || status === 'ready' ? (
        <span className="mx-1.5 flex items-center">
          {result.in_library ? <Check className="h-4 w-4 text-success" aria-hidden="true" /> :
            <Clock className="h-4 w-4 text-text-subtle" aria-hidden="true" />}
          <span className="sr-only">{result.in_library ? 'In your library' : 'Temporarily cached'}</span>
        </span>
      ) : null}

      {showProvider ? (
        <ProviderBadge provider={result.provider} name={providerName} />
      ) : null}
    </span>
  )

  const row = (
      <div className="flex items-center">
        {selectable && onSelectChange ? (
          <SelectCheckbox
            checked={selected}
            label={`Select ${result.title} by ${result.artist_name}`}
            onChange={onSelectChange}
          />
        ) : null}

        <div className="min-w-0 flex-1">
          <TrackRow
            title={result.title}
            artistName={result.artist_name}
            artistHref={searchArtistHref(result)}
            albumName={result.album_name}
            durationMs={result.duration_ms}
            coverUrl={result.artwork_url}
            isActive={isActive}
            isPlaying={isActive && isPlaying}
            status={status}
            progress={progress}
            onPlay={() => onAction('play')}
            isFavorite={isFavorite}
            onToggleFavorite={onToggleFavorite}
            trailing={trailing}
            menuItems={[
              ...songActions({ share: () => { void saveAndShare(result).catch((error: Error) => toast.error(error.message)) }, shareLabel: result.in_library ? "Share" : "Save to library & share", playNext: () => onAction('next'), addToQueue: () => onAction('queue'),
                addToPlaylist: onAddToPlaylist, addToLibrary: () => onAction('download'), inLibrary: result.in_library, busy }),
              ...(result.preview_url ? [{ label: previewing ? 'Stop preview' : 'Preview song', context: true, icon: Volume2, onSelect: () => previewing ? stopPreview() : startPreview({ key: previewKey, title: result.title, artistName: result.artist_name, url: result.preview_url! }) }] : []),
              ...(onDismissSuggestion ? [{ label: 'Dismiss suggestion', context: true, icon: X, onSelect: onDismissSuggestion }] : []),
              ...(onStartSelecting && !selectable
                ? [{ label: 'Select songs', context: true, icon: CircleCheck, onSelect: onStartSelecting }]
                : []),
            ]}
          />
        </div>
      </div>
  )
  return (
    <li>
      {wrapRow ? wrapRow(row) : row}
      {error ? (
        <ErrorNotice onRetry={error.retry} onDismiss={onDismissError}>{error.message}</ErrorNotice>
      ) : null}
    </li>
  )
}
